import React from "react";
import {
  BASE_UNITS,
  PACK_UNITS,
  formStyles,
} from "../../../components/Inventory-Stocks & Orders/Admin/Products/catalogConstants";

interface ProductPackagingDtlsTabProps {
  data: any;
  onChange: (field: string, value: any) => void;
  isEdit: boolean;
  onUpdate?: () => void;
  isLoading?: boolean;
}

/**
 * Packaging as stored on product_pkgn_dtls.
 *
 * The pack is described twice on purpose: `packagingText` is the human line
 * printed on the pack, while pack unit + quantity + base unit are the machine
 * readable version the stock and order maths use.
 */
export const ProductPackagingDtlsTab: React.FC<
  ProductPackagingDtlsTabProps
> = ({ data, onChange, isEdit, onUpdate, isLoading }) => {
  return (
    <div className="space-y-6">
      <section className={formStyles.sectionCard}>
        <h4 className={`${formStyles.sectionTitle} mb-4`}>Pack Description</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className={formStyles.lableStyle}>Packaging Text</label>
            <input
              type="text"
              value={data.packagingText}
              onChange={(e) => onChange("packagingText", e.target.value)}
              className={formStyles.inputStyle}
              placeholder='e.g. "Strip of 10 tablets"'
            />
            <p className="text-xs text-gray-400 mt-1">
              The line as printed on the pack. Shown to patients as-is.
            </p>
          </div>

          <div>
            <label className={formStyles.lableStyle}>Pack Type</label>
            <input
              type="text"
              value={data.packType}
              onChange={(e) => onChange("packType", e.target.value)}
              className={formStyles.inputStyle}
              placeholder="e.g. Blister, Carton"
            />
          </div>

          <div>
            <label className={formStyles.lableStyle}>Pack Unit</label>
            <select
              value={data.packUnit}
              onChange={(e) => onChange("packUnit", e.target.value)}
              className={formStyles.inputStyle}
            >
              <option value="">Select pack unit</option>
              {PACK_UNITS.map((unit) => (
                <option key={unit} value={unit}>
                  {unit}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">
              What one sellable pack is.
            </p>
          </div>
        </div>
      </section>

      <section className={formStyles.sectionCard}>
        <h4 className={`${formStyles.sectionTitle} mb-4`}>Contents</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={formStyles.lableStyle}>Pack Quantity</label>
            <input
              type="number"
              step="any"
              value={data.packQuantity}
              onChange={(e) => onChange("packQuantity", e.target.value)}
              className={formStyles.inputStyle}
              placeholder="e.g. 10"
            />
          </div>

          <div>
            <label className={formStyles.lableStyle}>Base Unit</label>
            <select
              value={data.baseUnit}
              onChange={(e) => onChange("baseUnit", e.target.value)}
              className={formStyles.inputStyle}
            >
              <option value="">Select base unit</option>
              {BASE_UNITS.map((unit) => (
                <option key={unit} value={unit}>
                  {unit}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">
              What one dispensable item is.
            </p>
          </div>

          <div>
            <label className={formStyles.lableStyle}>Multipack Count</label>
            <input
              type="number"
              min={1}
              value={data.multipackCount}
              onChange={(e) => onChange("multipackCount", e.target.value)}
              className={formStyles.inputStyle}
              placeholder="1"
            />
            <p className="text-xs text-gray-400 mt-1">
              Packs inside one sold unit. 1 for a single pack.
            </p>
          </div>

          <div>
            <label className={formStyles.lableStyle}>Free Quantity</label>
            <input
              type="number"
              step="any"
              value={data.offerFreeQty}
              onChange={(e) => onChange("offerFreeQty", e.target.value)}
              className={formStyles.inputStyle}
              placeholder="e.g. 2"
            />
          </div>

          <div className="md:col-span-2">
            <label className={formStyles.lableStyle}>Combo Items</label>
            <input
              type="text"
              value={data.comboItems}
              onChange={(e) => onChange("comboItems", e.target.value)}
              className={formStyles.inputStyle}
              placeholder="What else is in the box, for combo packs"
            />
          </div>
        </div>

        {(data.packQuantity || data.baseUnit) && (
          <p className="text-sm text-emerald-700 font-medium mt-4">
            {data.multipackCount && Number(data.multipackCount) > 1
              ? `${data.multipackCount} × `
              : ""}
            {data.packUnit || "Pack"} of {data.packQuantity || "?"}{" "}
            {data.baseUnit || "units"}
          </p>
        )}
      </section>

      {isEdit && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onUpdate}
            disabled={isLoading}
            className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition disabled:opacity-50"
          >
            {isLoading ? "Updating..." : "Update Packaging"}
          </button>
        </div>
      )}
    </div>
  );
};
